import { useMemo } from 'react';

import { isRowApproved } from './approvalQueueModel';
import type { ChecklistStatus } from './shared/checklistUtils';
import type { BlackoutRowView } from './tabs/blackoutDates/types';
import type { BundledExperienceRowView } from './tabs/bundledExperiences/types';
import type { CampaignRowView } from './tabs/campaign/types';
import type { DealOptionInclusionRowView } from './tabs/dealOptionsInclusions/types';
import type { DiscountsMarginRowView } from './tabs/discountsMargin/types';
import type { EgsCapacityRowView, EgsMarginRowView } from './tabs/egs/types';
import type { SetupRowView } from './tabs/setup/types';
import type { SurchargeCheckRowView, SurchargePeriodRowView } from './tabs/surcharges/types';
import type { TaxesPaymentsRowView } from './tabs/taxesPayments/types';
import type { ChecklistCategoryId, ManualOverrideState } from './types';

function countNeedsApproval(
  rows: Array<{ id: string; status: ChecklistStatus }>,
  manualOverrides: ManualOverrideState,
) {
  return rows.filter((row) => row.status !== 'blocked' && !isRowApproved(row, manualOverrides)).length;
}

export function useChecklistSummary({
  baseSurchargesRows,
  campaignRows,
  discountsMarginRows,
  blackoutRows,
  bundledExperienceRows,
  taxesPaymentsRows,
  capacityRows,
  dealOptionsInclusionsRows,
  manualOverrides,
  marginRows,
  setupRows,
  surchargeTableRows,
}: {
  baseSurchargesRows: Array<SurchargeCheckRowView>;
  campaignRows: Array<CampaignRowView>;
  discountsMarginRows: Array<DiscountsMarginRowView>;
  blackoutRows: Array<BlackoutRowView>;
  bundledExperienceRows: Array<BundledExperienceRowView>;
  taxesPaymentsRows: Array<TaxesPaymentsRowView>;
  capacityRows: Array<EgsCapacityRowView>;
  dealOptionsInclusionsRows: Array<DealOptionInclusionRowView>;
  manualOverrides: ManualOverrideState;
  marginRows: Array<EgsMarginRowView>;
  setupRows: Array<SetupRowView>;
  surchargeTableRows: Array<SurchargePeriodRowView>;
}) {
  return useMemo(() => {
    const allRows = [
      ...setupRows,
      ...campaignRows,
      ...discountsMarginRows,
      ...bundledExperienceRows,
      ...marginRows,
      ...capacityRows,
      ...dealOptionsInclusionsRows,
      ...blackoutRows,
      ...taxesPaymentsRows,
      ...baseSurchargesRows,
      ...surchargeTableRows,
    ];
    const approvedCount = allRows.filter((row) => isRowApproved(row, manualOverrides)).length;
    const blockedCount = allRows.filter((row) => row.status === 'blocked').length;
    const totalCount = allRows.length;
    const needsApprovalCount = totalCount - approvedCount - blockedCount;

    const categoryCounts: Record<ChecklistCategoryId, number> = {
      setup: countNeedsApproval(setupRows, manualOverrides),
      campaign: countNeedsApproval(campaignRows, manualOverrides),
      discountsMargin: countNeedsApproval(discountsMarginRows, manualOverrides),
      taxesPayments: countNeedsApproval(taxesPaymentsRows, manualOverrides),
      dealOptionsInclusions: countNeedsApproval(dealOptionsInclusionsRows, manualOverrides),
      surcharges:
        countNeedsApproval(baseSurchargesRows, manualOverrides) +
        countNeedsApproval(surchargeTableRows, manualOverrides),
      blackoutDates: countNeedsApproval(blackoutRows, manualOverrides),
      egs: countNeedsApproval(marginRows, manualOverrides) + countNeedsApproval(capacityRows, manualOverrides),
      bundledExperiences: countNeedsApproval(bundledExperienceRows, manualOverrides),
    };

    return {
      approvedCount,
      categoryCounts,
      needsApprovalCount,
      totalCount,
    };
  }, [
    baseSurchargesRows,
    campaignRows,
    discountsMarginRows,
    blackoutRows,
    bundledExperienceRows,
    taxesPaymentsRows,
    capacityRows,
    dealOptionsInclusionsRows,
    manualOverrides,
    marginRows,
    setupRows,
    surchargeTableRows,
  ]);
}
